import { perpPriceDecimals } from "../numbers/perp-price-rule";
import { formatUsd } from "./format-usd";
import Decimal from "decimal.js";

/** A price always reads as money, however coarse the venue's grid. */
const MIN_PRICE_DECIMALS = 2;

/**
 * Fraction digits for a market PRICE, from the venue's own grid rather than a
 * fixed 2.
 *
 * A flat 2 decimals is right for money that is counted (PnL, balances,
 * notional) and wrong for a price that is quoted: at 2dp a $0.0561 market reads
 * `$0.06`, and at 0dp it reads `$0`.
 *
 * The grid is the perp price rule — ≤5 significant figures and ≤(6 −
 * szDecimals) decimals — which is what the backend's own `tickSize` states for
 * every paper pair. Never more digits than the value carries, and never fewer
 * than cents.
 *
 * `szDecimals` is the market's size precision, as the pair catalog states it.
 * When it is unknown the rule is evaluated at its finest grid (`szDecimals` 0,
 * so 6 decimals): a deliberate widest-cap, since the alternative — assuming a
 * coarse grid — is what hides digits. It can only ever show digits the value
 * already carries; it never invents precision.
 */
export function priceFractionDigits(value: number | string | Decimal, szDecimals?: number | null): number {
  let price: Decimal;
  try {
    price = new Decimal(value).abs();
  } catch {
    return MIN_PRICE_DECIMALS;
  }
  if (!price.isFinite() || price.isZero()) return MIN_PRICE_DECIMALS;

  const allowed = perpPriceDecimals(price, szDecimals ?? 0);
  return Math.max(MIN_PRICE_DECIMALS, Math.min(allowed, price.decimalPlaces()));
}

/**
 * Format a market price as `"$…"` at the venue's own precision — `$86,000.00`,
 * `$3,456.78`, `$0.0561`. See {@link priceFractionDigits}.
 *
 * For money that is counted rather than quoted (PnL, balances, notional, fees)
 * use `formatUsd`: those are 2dp by definition, and widening them is noise.
 */
export function formatPriceUsd(value: number | string | Decimal, szDecimals?: number | null): string {
  return formatUsd(value, { decimalPlaces: priceFractionDigits(value, szDecimals) });
}
